import { stringify } from "query-string";
import { ApiEndpoint, ApiSubresourceEndpoint } from "./endpoints";
import { conf } from "../conf";
import { retrieveToken } from "./token-storage";
import { RealtimeSync } from "../sync";
import { store } from "../store";
import { setLoginStatus } from "../actions/store/app-actions";

export enum Method {
	GET = "GET",
	POST = "POST",
	PUT = "PUT",
	PATCH = "PATCH",
	DELETE = "DELETE",
	HEAD = "HEAD"
}

export interface ApiResponse {
	paginationCount: string;
	paginationPage: string;
	paginationLimit: string;
	data: any;
}

type HTTPHeader = {
	[key: string]: string;
};

const BASE_URL = "/api/v1";

const createHeaders = (): HTTPHeader => {
	const { CSRFToken } = conf;
	const jwtToken = retrieveToken();
	const syncUUID = RealtimeSync.getUUID();

	const headers: HTTPHeader = {};

	if (CSRFToken) {
		headers["X-CSRF-Token"] = CSRFToken;
	}

	if (jwtToken) {
		headers["Authorization"] = `Bearer ${jwtToken}`;
	}

	if (syncUUID) {
		headers["Sync-UUID"] = syncUUID;
	}

	return headers;
};

const transformResponsePayload = async (
	method: Method,
	response: Response,
	rawResponse = false
): Promise<ApiResponse> => {
	const resData = {
		paginationCount: response.headers.get("Pagination-Count"),
		paginationPage: response.headers.get("Pagination-Page"),
		paginationLimit: response.headers.get("Pagination-Limit")
	};

	if (rawResponse) {
		return {
			...resData,
			data: response
		};
	}

	if (method === Method.DELETE) {
		return {
			...resData,
			data: await response.text()
		};
	}

	return {
		...resData,
		data: await response.json()
	};
};

const makeRequest = async (
	url: string,
	method: Method,
	data?: {},
	rawResponse = false
): Promise<ApiResponse> => {
	//const controller = new AbortController();

	const timeoutPromise = new Promise(resolve =>
		setTimeout(() => resolve(false), conf.requestTimeout)
	);

	const responsePromise = fetch(url, {
		method,
		body: data ? JSON.stringify(data) : null,
		headers: createHeaders()
		//signal: controller.signal
	});

	// Start both request and timeout simultaneously
	// The first promise to resolve or reject will advance execution
	const response = (await Promise.race([responsePromise, timeoutPromise])) as
		| Response
		| false;

	// If response is not false, the request was successful
	if (response !== false) {
		// Handle session expiry
		if (response.status === 401) {
			store.dispatch(setLoginStatus(false));
		}

		// Throw error if response code is >= 400
		if (response.status >= 400) {
			throw new Error(response.statusText);
		}

		return await transformResponsePayload(method, response, rawResponse);
	} else {
		// If response was false, the request timeout was reached
		// Abort request and throw error

		//controller.abort();
		throw new Error("Request timeout");
	}
};

export const dispatchRequest = async (
	endpoint: ApiEndpoint,
	method: Method,
	id?: string,
	data?: {},
	params?: {},
	rawResponse = false
): Promise<ApiResponse> => {
	const path = id ? `${endpoint}/${id}` : endpoint;
	const qParams = params ? "?" + stringify(params) : "";

	const url = BASE_URL + path + qParams;

	return await makeRequest(url, method, data, rawResponse);
};

export const dispatchSubresourceRequest = async (
	primaryEndpoint: ApiEndpoint,
	subresourceEndpoint: ApiSubresourceEndpoint,
	method: Method,
	id?: string,
	subresourceId?: string,
	data?: {},
	params?: {}
): Promise<ApiResponse> => {
	let idSegment = id ? `/${id}` : "";
	let path = `${primaryEndpoint}${idSegment}/${subresourceEndpoint}`;
	const qParams = params ? "?" + stringify(params) : "";

	if (subresourceId) {
		path += "/" + subresourceId;
	}

	const url = BASE_URL + path + qParams;

	return await makeRequest(url, method, data);
};
